import type { ChatPanel } from '@affine/core/blocksuite/ai/chat-panel';
import type {
  WorkspaceViewContextReader,
  WorkspaceViewSelection,
} from '@affine/core/blocksuite/ai/components/ai-chat-content/type';
import type {
  UiObjectRef,
  UiSemanticElement,
  WorkspaceSurfaceContent,
  WorkspaceViewReadResult,
  WorkspaceViewRevision,
  WorkspaceViewScope,
  WorkspaceViewSnapshot,
} from '@streamlain/copilot-realtime-contract';
import {
  WORKSPACE_VIEW_MAX_INTERACTIONS,
  WORKSPACE_VIEW_MAX_NAVIGATION_VISITS,
  WORKSPACE_VIEW_MAX_REF_VISITS,
  WORKSPACE_VIEW_MAX_ROUTE_CODE_POINTS,
  WORKSPACE_VIEW_MAX_TITLE_CODE_POINTS,
} from '@streamlain/copilot-realtime-contract';
import { type Framework, LiveData, Service } from '@toeverything/infra';
import {
  combineLatest,
  distinctUntilChanged,
  map,
  type Observable,
} from 'rxjs';

import { AffineCommandRegistry } from '../../../commands/registry/registry';
import type { View } from '../../workbench/entities/view';
import { WorkbenchService } from '../../workbench/services/workbench';
import { WorkspaceScope, WorkspaceService } from '../../workspace';
import { AIOperatorPresentationStore } from '../app-operator/operator-presentation';
import { getAssistantPresenceStore } from './assistant-presence-store';
import type { ContextDockSource } from './context-dock';
import { isUnderCoveringLayer } from './semantic-dom-context';
import { collectSemanticOverlays } from './semantic-dom-overlays';
import { projectSurfaceInteractions } from './surface-interactions';
import type { SurfaceObjectTarget } from './surface-objects';
import { viewReadClock } from './view-read-clock';
import { WorkspaceNavigationCatalog } from './workspace-navigation-catalog';
import { WorkspaceNavigationExecutor } from './workspace-navigation-executor';
import { WorkspaceOperationExecutor } from './workspace-operation-executor';
import {
  fitWorkspaceViewSnapshot,
  fitWorkspaceViewText,
} from './workspace-view-budget';

/** The route a view's workbench stands at; navigation inside a view moves it. */
function workbenchRoute(view: View): string {
  const location = view.location$.value;
  return location.pathname + location.search + location.hash;
}

export type WorkspaceSurfaceSupplement = Pick<
  WorkspaceSurfaceContent,
  'elements' | 'interactions'
>;
export interface WorkspaceSurfaceOptions {
  kind: 'base' | 'surface' | 'region' | 'dialog';
  isActive?: () => boolean;
  /** Optional semantic work, never part of render/click/metadata snapshot reads. */
  collect?: (
    signal: AbortSignal,
    isCurrent: () => boolean,
    deadlineMs: number
  ) => Promise<WorkspaceSurfaceSupplement>;
  /** A drawn object this owner listed (canvas element, graph node), as drawn now. */
  locate?: (key: string) => SurfaceObjectTarget | null;
  /** The element this owner's drawn surface takes pointer input on, while it draws. */
  surfaceRoot?: () => Element | null;
  /**
   * Where a region stands in a read that is cut to size: the view's tools before its content,
   * the app's shell around the view after it.
   */
  place?: 'tools' | 'shell';
  /** True while the source holds interactions the user captured for a send. */
  holdsCaptures?: () => boolean;
}
export interface WorkspaceViewContextPort {
  register(
    scope: WorkspaceViewScope,
    ownerId: string,
    read: () => WorkspaceSurfaceContent,
    options?: WorkspaceSurfaceOptions
  ): () => void;
  invalidate(
    scope: WorkspaceViewScope,
    ownerId: string,
    expectedRead?: () => WorkspaceSurfaceContent
  ): WorkspaceViewRevision | null;
  readRevision(
    scope: WorkspaceViewScope,
    ownerId: string,
    expectedRead?: () => WorkspaceSurfaceContent
  ): WorkspaceViewRevision | null;
  snapshot(
    scope: WorkspaceViewScope,
    expected?: WorkspaceViewRevision
  ): WorkspaceViewReadResult;
  wasIssued(
    scope: WorkspaceViewScope,
    revision: WorkspaceViewRevision
  ): boolean;
  dispose(): void;
}
interface SurfaceOwner {
  read: () => WorkspaceSurfaceContent;
  options: WorkspaceSurfaceOptions;
}
interface DockOwner {
  source: ContextDockSource;
  options: WorkspaceSurfaceOptions;
}
interface ViewContextOwner {
  view: View;
  navigationPresentation: AIOperatorPresentationStore;
  revision: WorkspaceViewRevision;
  /** Epochs this view issued, with their highest sequence (bounded, oldest first). */
  issued: Map<string, number>;
  sources: Map<string, SurfaceOwner>;
  /** Owners' docks by owner id; an owner may offer a dock with or without view context. */
  docks: Map<string, DockOwner>;
  fingerprint: string | null;
  releaseBase: () => void;
}
// Canonical equality ignores object-key insertion order, preserving array order.
function canonical(value: unknown): string {
  return JSON.stringify(value, (_key, entry: unknown) => {
    if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
      return Object.fromEntries(
        Object.entries(entry).sort(([a], [b]) => a.localeCompare(b))
      );
    }
    return entry;
  });
}
function revision(): WorkspaceViewRevision {
  return { epoch: crypto.randomUUID(), sequence: 1 };
}
const MAX_ISSUED_EPOCHS = 32;
/** A surface under a covering layer (a fullscreen view) draws nothing the user sees. */
const drawsUnderCoveringLayer = ({ surfaceRoot }: WorkspaceSurfaceOptions) => {
  const drawn = surfaceRoot?.();
  return !!drawn && isUnderCoveringLayer(drawn);
};

const regionRank = ({ place }: WorkspaceSurfaceOptions) =>
  place === 'tools' ? 0 : place === 'shell' ? 2 : 1;
/** DOM sources one request reads. */
const MAX_DOM_SOURCES = 8;
/**
 * What one source may take of a read. Each has its own time: a canvas that takes long over its
 * drawn objects costs the header and the panes beside it nothing.
 */
const SOURCE_READ_MS = 50;

/** One workspace-owned revision authority, always reading the real view owners. */
export class WorkspaceViewContextService
  extends Service
  implements WorkspaceViewContextPort
{
  private readonly views = new Map<string, ViewContextOwner>();
  private disposed = false;
  readonly changes$ = new LiveData(0);
  readonly navigation: WorkspaceNavigationCatalog;
  readonly navigationPort: WorkspaceNavigationExecutor;
  readonly operationPort: WorkspaceOperationExecutor;
  constructor(
    private readonly workbenchService: WorkbenchService,
    private readonly workspaceService: WorkspaceService
  ) {
    super();
    const workbench = workbenchService.workbench;
    this.navigation = new WorkspaceNavigationCatalog(this, () => ({
      workspaceId: this.workspaceId,
      viewId: workbench.activeView$.value.id,
    }));
    this.navigationPort = new WorkspaceNavigationExecutor(this);
    this.operationPort = new WorkspaceOperationExecutor(this);
    const commandSubscription = AffineCommandRegistry.changes$.subscribe(() =>
      this.changed()
    );
    const viewsSubscription = workbench.views$.subscribe(views => {
      for (const [id, owner] of this.views) {
        if (!views.includes(owner.view)) {
          owner.navigationPresentation.clear();
          owner.releaseBase();
          this.views.delete(id);
        }
      }
      for (const view of views) {
        if (this.views.has(view.id)) continue;
        const initial = revision();
        const owner: ViewContextOwner = {
          view,
          navigationPresentation: new AIOperatorPresentationStore(),
          revision: initial,
          issued: new Map([[initial.epoch, initial.sequence]]),
          sources: new Map(),
          docks: new Map(),
          fingerprint: null,
          releaseBase: () => {},
        };
        this.views.set(view.id, owner);
        owner.sources.set('workbench', {
          options: { kind: 'base' },
          read: () => {
            return {
              surfaceId: 'workbench',
              route: workbenchRoute(view),
              title: view.title$.value,
              summary: '',
              refs: [],
              elements: [],
              interactions: [],
              actions: [],
            };
          },
        });
        const subscription = combineLatest([
          view.location$,
          view.title$,
        ]).subscribe(() =>
          this.invalidate(
            { workspaceId: this.workspaceId, viewId: view.id },
            'workbench'
          )
        );
        owner.releaseBase = () => subscription.unsubscribe();
      }
      this.changed();
    });
    const activeSubscription = workbench.activeView$.subscribe(() => {
      // Focus changes invalidate pending requests without transferring data across views.
      for (const viewOwner of this.views.values())
        viewOwner.navigationPresentation.clear();
      const owner = this.views.get(workbench.activeView$.value.id);
      if (owner) this.bump(owner);
      AffineCommandRegistry.invalidate();
      this.changed();
    });
    this.disposables.push(
      () => viewsSubscription.unsubscribe(),
      () => activeSubscription.unsubscribe(),
      () => commandSubscription.unsubscribe(),
      () => this.navigation.dispose(),
      () => this.navigationPort.dispose(),
      () => this.operationPort.dispose()
    );
  }
  get workspaceId() {
    return this.workspaceService.workspace.id;
  }
  /** Scope guard for ordinary UI activation; never invokes a context producer. */
  isActiveScope(scope: WorkspaceViewScope): boolean {
    return (
      !this.disposed &&
      scope.workspaceId === this.workspaceId &&
      !!this.owner(scope) &&
      this.workbenchService.workbench.activeView$.value.id === scope.viewId
    );
  }
  getNavigationPresentation(
    scope: WorkspaceViewScope
  ): AIOperatorPresentationStore | null {
    if (
      this.disposed ||
      this.workbenchService.workbench.activeView$.value.id !== scope.viewId
    )
      return null;
    return this.owner(scope)?.navigationPresentation ?? null;
  }
  readActive: WorkspaceViewContextReader = Object.assign(
    (selection?: WorkspaceViewSelection): WorkspaceViewReadResult =>
      this.snapshot(
        {
          workspaceId: this.workspaceId,
          viewId: this.workbenchService.workbench.activeView$.value.id,
        },
        undefined,
        selection
      ),
    {
      place: () => {
        if (this.disposed) return null;
        const view = this.workbenchService.workbench.activeView$.value;
        const owner = this.views.get(view.id);
        return {
          workspaceId: this.workspaceId,
          viewId: view.id,
          route: workbenchRoute(view),
          revision: owner ? { ...owner.revision } : undefined,
          sources: owner ? [...owner.sources.keys()] : [],
          captured: owner
            ? [...owner.sources]
                .filter(([, source]) => source.options.holdsCaptures?.())
                .map(([id]) => id)
            : [],
        };
      },
      collect: (
        selection: WorkspaceViewSelection | undefined,
        signal: AbortSignal
      ) => this.collectActive(selection, signal),
      settle: (signal: AbortSignal) => this.settleActive(signal),
      isCurrent: (snapshot: WorkspaceViewSnapshot) => {
        const owner = this.owner(snapshot);
        return (
          !this.disposed &&
          this.isActiveScope(snapshot) &&
          !!owner &&
          owner.revision.epoch === snapshot.revision.epoch &&
          owner.revision.sequence === snapshot.revision.sequence
        );
      },
    }
  );

  private settleActive(signal: AbortSignal): Promise<boolean> {
    signal.throwIfAborted();
    const place = this.readActive.place();
    if (!place) return Promise.resolve(false);
    const owner = this.owner(place);
    if (!owner) return Promise.resolve(false);
    return new Promise<boolean>(resolve => {
      let frame: number | undefined;
      let done = false;
      let revision = owner.revision;
      let subscription: { unsubscribe(): void } | undefined;
      const finish = (current: boolean) => {
        if (done) return;
        done = true;
        if (frame !== undefined) window.cancelAnimationFrame(frame);
        subscription?.unsubscribe();
        signal.removeEventListener('abort', abort);
        resolve(current);
      };
      const abort = () => {
        finish(false);
        // Abort is checked again by the send owner after the wait.
      };
      const current = () => {
        const now = this.readActive.place();
        return (
          !!now &&
          this.isActiveScope(place) &&
          this.owner(place) === owner &&
          now.route === place.route &&
          place.captured.every(id => now.captured.includes(id))
        );
      };
      const render = () => {
        if (frame !== undefined) window.cancelAnimationFrame(frame);
        revision = owner.revision;
        frame = window.requestAnimationFrame(() => {
          frame = window.requestAnimationFrame(() => {
            if (!current()) return finish(false);
            if (revision !== owner.revision) return render();
            finish(true);
          });
        });
      };
      signal.addEventListener('abort', abort, { once: true });
      subscription = this.changes$.subscribe(() => {
        if (!current()) finish(false);
        else if (revision !== owner.revision) render();
      });
      if (done) subscription.unsubscribe();
      else render();
    });
  }

  private async collectActive(
    selection: WorkspaceViewSelection | undefined,
    signal: AbortSignal
  ): Promise<WorkspaceViewReadResult> {
    signal.throwIfAborted();
    const baseline = this.readActive(selection);
    if (!baseline.ok) return baseline;
    const scope = {
      workspaceId: baseline.snapshot.workspaceId,
      viewId: baseline.snapshot.viewId,
    };
    const owner = this.owner(scope)!;
    const stale = (): WorkspaceViewReadResult => ({
      ok: false,
      reason: 'stale',
      scope,
    });
    const current = () =>
      !signal.aborted &&
      this.views.get(scope.viewId) === owner &&
      this.readActive.isCurrent!(baseline.snapshot);
    const active = [...owner.sources].filter(
      ([, source]) => source.options.isActive?.() ?? true
    );
    const dialog = active
      .filter(([, source]) => source.options.kind === 'dialog')
      .at(-1);
    // Native model context wins. Open popups, then the drawn objects of the
    // surfaces, then the optional DOM sources, each within its own time.
    const sources = dialog
      ? [dialog]
      : [
          ...active.filter(
            ([, source]) =>
              source.options.kind === 'surface' &&
              !!source.options.collect &&
              !drawsUnderCoveringLayer(source.options)
          ),
          ...active
            .filter(([, source]) => source.options.kind === 'region')
            .sort(
              ([, a], [, b]) => regionRank(a.options) - regionRank(b.options)
            ),
        ];
    const elements = [...baseline.snapshot.elements];
    const keys = new Set(elements.map(element => element.key));
    const add = (supplement: readonly UiSemanticElement[]) => {
      for (
        let index = 0;
        index < supplement.length &&
        elements.length < WORKSPACE_VIEW_MAX_NAVIGATION_VISITS;
        index++
      ) {
        const element = supplement[index];
        if (keys.has(element.key)) continue;
        keys.add(element.key);
        elements.push(element);
      }
    };
    const interactions = [...baseline.snapshot.interactions];
    // An open dialog whose context source reads what it shows describes itself; read as a
    // popup it would come twice. Every other open popup is read from the DOM, a dialog that
    // only names itself included: what the user sees in it is there to be operated.
    if (!dialog?.[1].options.collect)
      add(
        await collectSemanticOverlays(window.document, scope, {
          signal,
          isCurrent: current,
          deadline: viewReadClock.now() + SOURCE_READ_MS,
        })
      );
    signal.throwIfAborted();
    for (const [id, source] of sources.slice(0, MAX_DOM_SOURCES)) {
      if (!current()) return stale();
      if (!source.options.collect) continue;
      const sourceCurrent = () =>
        current() &&
        owner.sources.get(id) === source &&
        (source.options.isActive?.() ?? true);
      const supplement = await source.options.collect(
        signal,
        sourceCurrent,
        SOURCE_READ_MS
      );
      signal.throwIfAborted();
      if (!sourceCurrent()) return stale();
      add(supplement.elements);
      interactions.push(
        ...supplement.interactions.slice(0, WORKSPACE_VIEW_MAX_INTERACTIONS)
      );
    }
    if (!current()) return stale();
    const fitted = fitWorkspaceViewSnapshot({
      ...baseline.snapshot,
      elements,
      interactions: projectSurfaceInteractions(interactions),
    });
    return fitted.ok
      ? { ok: true, snapshot: fitted.snapshot }
      : { ok: false, reason: fitted.reason, scope };
  }
  register(
    scope: WorkspaceViewScope,
    ownerId: string,
    read: () => WorkspaceSurfaceContent,
    options: WorkspaceSurfaceOptions = { kind: 'surface' }
  ): () => void {
    const owner = this.owner(scope);
    if (this.disposed || !owner || !ownerId.trim() || ownerId === 'workbench')
      throw new Error('Unavailable workspace view context owner');
    const source = { read, options };
    // Insertion order follows real mounts/focus; remounts move only this owner's slot.
    owner.sources.delete(ownerId);
    owner.sources.set(ownerId, source);
    this.advance(owner, revision());
    owner.fingerprint = null;
    this.changed();
    return () => {
      if (
        this.disposed ||
        this.views.get(scope.viewId) !== owner ||
        owner.sources.get(ownerId) !== source
      )
        return;
      owner.sources.delete(ownerId);
      this.bump(owner);
      this.changed();
    };
  }
  /**
   * Offers an owner's dock for the view until the returned release. The dock of the view's focused
   * owner shows beside the AI island (dialog > surface > base, the latest mounted first); an owner
   * with view context and a dock uses the same owner id for both.
   */
  provideDock(
    scope: WorkspaceViewScope,
    ownerId: string,
    source: ContextDockSource,
    options: WorkspaceSurfaceOptions = { kind: 'surface' }
  ): () => void {
    const owner = this.owner(scope);
    if (this.disposed || !owner || !ownerId.trim())
      throw new Error('Unavailable workspace view dock owner');
    const dock = { source, options };
    owner.docks.delete(ownerId);
    owner.docks.set(ownerId, dock);
    this.changed();
    return () => {
      if (
        this.disposed ||
        this.views.get(scope.viewId) !== owner ||
        owner.docks.get(ownerId) !== dock
      )
        return;
      owner.docks.delete(ownerId);
      this.changed();
    };
  }
  /** The focused owner's dock; null while it offers none (the island shows alone). */
  focusedDock(scope: WorkspaceViewScope): ContextDockSource | null {
    const owner = this.owner(scope);
    if (this.disposed || !owner) return null;
    const owners = new Map<string, WorkspaceSurfaceOptions>();
    for (const [id, source] of owner.sources) owners.set(id, source.options);
    for (const [id, dock] of owner.docks)
      if (!owners.has(id)) owners.set(id, dock.options);
    // A surface under a covering layer offers no dock, however late it mounted.
    const active = [...owners].filter(
      ([, options]) =>
        (options.isActive?.() ?? true) && !drawsUnderCoveringLayer(options)
    );
    const focused =
      active.filter(([, options]) => options.kind === 'dialog').at(-1) ??
      active.filter(([, options]) => options.kind === 'surface').at(-1) ??
      active.find(([, options]) => options.kind === 'base');
    return focused ? (owner.docks.get(focused[0])?.source ?? null) : null;
  }
  /** The focused owner's dock, live. */
  dock$(scope: WorkspaceViewScope): Observable<ContextDockSource | null> {
    return this.changes$.pipe(
      map(() => this.focusedDock(scope)),
      distinctUntilChanged()
    );
  }
  invalidate(
    scope: WorkspaceViewScope,
    ownerId: string,
    expectedRead?: () => WorkspaceSurfaceContent
  ): WorkspaceViewRevision | null {
    const owner = this.owner(scope);
    const source = owner?.sources.get(ownerId);
    if (
      this.disposed ||
      !owner ||
      !source ||
      (expectedRead && source.read !== expectedRead)
    )
      return null;
    this.bump(owner);
    this.changed();
    return { ...owner.revision };
  }
  /** A drawn object an active owner of the view lists under this key, as drawn now. */
  locateSurfaceObject(
    scope: WorkspaceViewScope,
    key: string
  ): SurfaceObjectTarget | null {
    const owner = this.owner(scope);
    if (this.disposed || !owner) return null;
    for (const source of owner.sources.values()) {
      if (!source.options.locate || !(source.options.isActive?.() ?? true))
        continue;
      const target = source.options.locate(key);
      if (target) return target;
    }
    return null;
  }
  /** The drawn surfaces the view's active owners show now: where a drag may let go. */
  surfaceRoots(scope: WorkspaceViewScope): Element[] {
    const owner = this.owner(scope);
    if (this.disposed || !owner) return [];
    const roots: Element[] = [];
    for (const source of owner.sources.values()) {
      if (!(source.options.isActive?.() ?? true)) continue;
      const root = source.options.surfaceRoot?.();
      if (root?.isConnected) roots.push(root);
    }
    return roots;
  }
  /** True when this view itself handed out the revision (never a fabricated one). */
  wasIssued(scope: WorkspaceViewScope, value: WorkspaceViewRevision): boolean {
    const latest = this.owner(scope)?.issued.get(value.epoch);
    return (
      latest !== undefined &&
      Number.isInteger(value.sequence) &&
      value.sequence >= 1 &&
      value.sequence <= latest
    );
  }
  /** Same revision authority without invoking any surface producer. */
  readRevision(
    scope: WorkspaceViewScope,
    ownerId: string,
    expectedRead?: () => WorkspaceSurfaceContent
  ): WorkspaceViewRevision | null {
    const owner = this.owner(scope);
    const source = owner?.sources.get(ownerId);
    if (
      !owner ||
      !source ||
      !this.isActiveScope(scope) ||
      (expectedRead && source.read !== expectedRead) ||
      !(source.options.isActive?.() ?? true)
    )
      return null;
    return { ...owner.revision };
  }
  snapshot(
    scope: WorkspaceViewScope,
    expected?: WorkspaceViewRevision,
    selection?: WorkspaceViewSelection
  ): WorkspaceViewReadResult {
    const fail = (
      reason: 'unavailable' | 'blocked' | 'stale' | 'invalid' | 'oversized'
    ): WorkspaceViewReadResult => ({
      ok: false,
      reason,
      scope: { workspaceId: scope.workspaceId, viewId: scope.viewId },
    });
    if (this.disposed) return fail('unavailable');
    if (scope.workspaceId !== this.workspaceId) return fail('blocked');
    const owner = this.owner(scope);
    if (
      !owner ||
      this.workbenchService.workbench.activeView$.value.id !== scope.viewId
    )
      return fail('stale');
    const expectedIsStale = () =>
      expected &&
      (expected.epoch !== owner.revision.epoch ||
        expected.sequence !== owner.revision.sequence);
    try {
      const active = [...owner.sources].filter(
        ([, source]) => source.options.isActive?.() ?? true
      );
      const focused =
        active
          .filter(([, source]) => source.options.kind === 'dialog')
          .at(-1) ??
        active
          .filter(([, source]) => source.options.kind === 'surface')
          .at(-1) ??
        active.find(([, source]) => source.options.kind === 'base');
      if (!focused) return fail('unavailable');
      const selected = [
        focused,
        ...(focused[1].options.kind === 'dialog'
          ? []
          : active.filter(([, source]) => source.options.kind === 'region')),
      ];
      const content = focused[1].read();
      if (
        typeof content.route !== 'string' ||
        typeof content.title !== 'string'
      )
        return fail(expectedIsStale() ? 'stale' : 'invalid');
      // Long titles/query routes stay usable context; the snapshot reports truncation.
      const route = fitWorkspaceViewText(
        content.route,
        WORKSPACE_VIEW_MAX_ROUTE_CODE_POINTS
      );
      const title = fitWorkspaceViewText(
        content.title,
        WORKSPACE_VIEW_MAX_TITLE_CODE_POINTS
      );
      // An impossible immutable binding cannot benefit from optional context work.
      const sourceLimit = 16;
      const contents = [
        content,
        ...selected.slice(1, sourceLimit).map(([, source]) => source.read()),
      ];
      let rawTruncated =
        selected.length > sourceLimit || route.truncated || title.truncated;
      const collect = <T>(
        read: (value: WorkspaceSurfaceContent) => readonly T[],
        limit: number,
        recent = false
      ): T[] => {
        const result: T[] = [];
        for (const value of contents) {
          const entries = read(value);
          const start = recent
            ? Math.max(0, entries.length - WORKSPACE_VIEW_MAX_INTERACTIONS)
            : 0;
          if (start > 0) rawTruncated = true;
          let index = start;
          for (; index < entries.length && result.length < limit; index++)
            result.push(entries[index]);
          if (index < entries.length) rawTruncated = true;
        }
        return result;
      };
      const refs = collect(value => value.refs, WORKSPACE_VIEW_MAX_REF_VISITS);
      let elements = collect(
        value => value.elements,
        WORKSPACE_VIEW_MAX_NAVIGATION_VISITS
      );
      const interactions = projectSurfaceInteractions(
        collect(
          value => value.interactions,
          sourceLimit * WORKSPACE_VIEW_MAX_INTERACTIONS,
          true
        )
      );
      const navigation = this.navigation.readContext(scope);
      if (!navigation.ok)
        return fail(expectedIsStale() ? 'stale' : navigation.reason);
      const actions = collect(
        value => value.actions,
        Math.max(
          0,
          WORKSPACE_VIEW_MAX_NAVIGATION_VISITS - navigation.actions.length
        )
      );
      if (selection) {
        if (
          selection.refs.length > WORKSPACE_VIEW_MAX_REF_VISITS ||
          selection.elements.length > WORKSPACE_VIEW_MAX_NAVIGATION_VISITS
        )
          return fail('oversized');
        const explicitIds = new Set(
          selection.refs.map(ref => JSON.stringify([ref.kind, ref.id]))
        );
        const primary = refs[0];
        const identity = (ref: UiObjectRef) =>
          JSON.stringify([ref.kind, ref.id]);
        const primaryId = primary && identity(primary);
        const requiredPrimary = selection.refs.find(
          ref => identity(ref) === primaryId
        );
        // The real primary surface stays first, including when it was also picked.
        const required = selection.refs.filter(
          ref => identity(ref) !== primaryId
        );
        refs.splice(
          0,
          refs.length,
          ...(primary ? [requiredPrimary ?? primary] : []),
          ...required,
          ...refs.filter(
            ref => ref !== primary && !explicitIds.has(identity(ref))
          )
        );
        const explicitKeys = new Set(
          selection.elements.map(element => element.key)
        );
        elements = [
          ...selection.elements,
          ...elements.filter(element => !explicitKeys.has(element.key)),
        ];
      }
      const projection: WorkspaceSurfaceContent = {
        truncated:
          rawTruncated ||
          navigation.truncated ||
          contents.some(value => value.truncated === true),
        surfaceId: content.surfaceId,
        route: route.value,
        title: title.value,
        summary: content.summary,
        refs,
        elements,
        interactions,
        actions: [...actions, ...navigation.actions],
      };
      let fitted = fitWorkspaceViewSnapshot({
        workspaceId: scope.workspaceId,
        viewId: scope.viewId,
        ...projection,
        schemaVersion: 1,
        revision: { ...owner.revision },
        truncated: projection.truncated === true,
      });
      if (!fitted.ok)
        return fail(
          expectedIsStale()
            ? 'stale'
            : fitted.reason === 'oversized'
              ? 'oversized'
              : 'invalid'
        );
      const fingerprintOf = (snapshot: WorkspaceViewSnapshot) => {
        const {
          schemaVersion: _schema,
          workspaceId: _workspace,
          viewId: _view,
          revision: _revision,
          ...boundedProjection
        } = snapshot;
        return canonical({
          owners: selected.slice(0, sourceLimit).map(([id]) => id),
          projection: boundedProjection,
        });
      };
      const fingerprint = fingerprintOf(fitted.snapshot);
      const contentChanged =
        owner.fingerprint !== null && owner.fingerprint !== fingerprint;
      if (contentChanged) {
        this.bump(owner);
        // A wider sequence (or a new epoch) also consumes the wire budget.
        // Refit only the bounded projection, without reading the sources again.
        fitted = fitWorkspaceViewSnapshot({
          ...fitted.snapshot,
          revision: { ...owner.revision },
        });
        // Publish the final normalized fingerprint before synchronous readers.
        owner.fingerprint = fitted.ok ? fingerprintOf(fitted.snapshot) : null;
        this.changed();
        if (!fitted.ok)
          return fail(
            expectedIsStale()
              ? 'stale'
              : fitted.reason === 'oversized'
                ? 'oversized'
                : 'invalid'
          );
      } else {
        owner.fingerprint = fingerprint;
      }
      // Fresh readers run before this guard, even when an observer did not fire.
      if (expectedIsStale()) return fail('stale');
      if (selection) {
        const retainedRefs = new Set(fitted.snapshot.refs.map(canonical));
        const retainedElements = new Map(
          fitted.snapshot.elements.map(element => [
            element.key,
            new Set((element.refs ?? []).map(canonical)),
          ])
        );
        if (
          selection.refs.some(ref => !retainedRefs.has(canonical(ref))) ||
          selection.elements.some(element => {
            const retained = retainedElements.get(element.key);
            return (
              !retained ||
              element.refs?.some(ref => !retained.has(canonical(ref)))
            );
          })
        )
          return fail('oversized');
      }
      return fitted;
    } catch {
      return fail('unavailable');
    }
  }
  private owner(scope: WorkspaceViewScope) {
    return scope.workspaceId === this.workspaceId
      ? this.views.get(scope.viewId)
      : undefined;
  }
  private bump(owner: ViewContextOwner) {
    this.advance(
      owner,
      owner.revision.sequence < Number.MAX_SAFE_INTEGER
        ? { ...owner.revision, sequence: owner.revision.sequence + 1 }
        : revision()
    );
  }
  private advance(owner: ViewContextOwner, next: WorkspaceViewRevision) {
    owner.revision = next;
    owner.issued.delete(next.epoch);
    owner.issued.set(next.epoch, next.sequence);
    if (owner.issued.size > MAX_ISSUED_EPOCHS)
      owner.issued.delete(owner.issued.keys().next().value!);
  }
  private changed() {
    this.changes$.next(this.changes$.value + 1);
  }
  override dispose() {
    if (this.disposed) return;
    this.disposed = true;
    super.dispose();
    for (const owner of this.views.values()) {
      owner.navigationPresentation.clear();
      owner.releaseBase();
    }
    this.views.clear();
    this.changed();
  }
}
export function configureWorkspaceViewContextModule(
  framework: Framework
): void {
  framework
    .scope(WorkspaceScope)
    .service(WorkspaceViewContextService, [WorkbenchService, WorkspaceService]);
}
/** CachedPanel receives a reader, never a copy in another domain's viewContext. */
export function bindWorkspaceViewChatPanelContext(
  service: WorkspaceViewContextService,
  panel: ChatPanel
): () => void {
  const read = Object.assign(
    (selection?: WorkspaceViewSelection) => service.readActive(selection),
    {
      place: service.readActive.place,
      collect: service.readActive.collect,
      settle: service.readActive.settle,
      isCurrent: service.readActive.isCurrent,
    }
  );
  panel.workspaceViewContextReader = read;
  panel.workspaceNavigationPort = service.navigationPort;
  // The history makes room for what the assistant operates, and for what a tool changes
  // in a document without operating the app.
  const releaseStart = service.operationPort.onStart(() => panel.minimize());
  const assistant = getAssistantPresenceStore(service.workspaceId);
  let touched = assistant.getSnapshot().touched;
  const releaseTouch = assistant.subscribe(() => {
    const next = assistant.getSnapshot().touched;
    if (next !== touched && next.length > 0)
      panel.minimize().catch(console.error);
    touched = next;
  });
  return () => {
    releaseStart();
    releaseTouch();
    if (panel.workspaceViewContextReader === read) {
      if (panel.workspaceNavigationPort === service.navigationPort)
        panel.workspaceNavigationPort = undefined;
      panel.workspaceViewContextReader = undefined;
    }
  };
}
